import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wallet, FileText, FileBarChart, Archive } from "lucide-react";
import { toast } from "sonner";

const money = (v) => `${Number(v || 0).toLocaleString("fr-FR")} Dhs`;
const STATUS_STYLE = { issued: "bg-sky-100 text-sky-700", partial: "bg-amber-100 text-amber-700", paid: "bg-emerald-100 text-emerald-700", cancelled: "bg-slate-200 text-slate-400", draft: "bg-slate-100 text-slate-500" };

export default function FamilyAccount() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canView = hasRole("super_admin", "direction", "administration", "finance");
  const [families, setFamilies] = useState([]);
  const [fid, setFid] = useState("");
  const [acc, setAcc] = useState(null);

  useEffect(() => { api.get("/families").then((r) => setFamilies(r.data)).catch(() => {}); }, []);
  const choose = async (id) => { setFid(id); try { const r = await api.get(`/finance/families/${id}/account`); setAcc(r.data); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const openPdf = async (path) => { try { const r = await api.get(path, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const previewStatement = () => { if (fid) openPdf(`/finance/families/${fid}/statement/pdf`); };
  const archiveStatement = async () => {
    if (!fid) return;
    try { const r = await api.post(`/finance/families/${fid}/statement/archive`, {}); toast.success(`${r.data.ref} — ${t("archived")}`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!canView) return <div className="p-8 text-center text-slate-400" data-testid="access-denied">{t("accessDenied")}</div>;

  return (
    <div data-testid="family-account-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3"><Wallet className="h-6 w-6 text-primary" /><h1 className="text-2xl font-display font-bold text-slate-900">{t("financeAccounts")}</h1></div>
        <Select value={fid} onValueChange={choose}><SelectTrigger className="w-64" data-testid="fa-family"><SelectValue placeholder={t("selectFamily")} /></SelectTrigger>
          <SelectContent>{families.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent></Select>
        {fid && <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-2" data-testid="fa-preview-statement" onClick={previewStatement}><FileBarChart className="h-4 w-4" />{t("previewStatement")}</Button>
          <Button className="gap-2" data-testid="fa-archive-statement" onClick={archiveStatement}><Archive className="h-4 w-4" />{t("archiveStatement")}</Button>
        </div>}
      </div>

      {!acc ? <p className="text-slate-400 text-center py-10">{t("selectFamily")}</p> : (<>
        <div className="grid sm:grid-cols-3 gap-3 mb-4" data-testid="fa-totals">
          <Card className="card-float border-slate-200 p-4"><div className="text-slate-500 text-sm">{t("invoiced")}</div><div className="text-2xl font-bold text-slate-900">{money(acc.totals.invoiced)}</div></Card>
          <Card className="card-float border-slate-200 p-4"><div className="text-slate-500 text-sm">{t("paid")}</div><div className="text-2xl font-bold text-emerald-600">{money(acc.totals.paid)}</div></Card>
          <Card className="card-float border-slate-200 p-4"><div className="text-slate-500 text-sm">{t("remaining")}</div><div className="text-2xl font-bold text-amber-600">{money(acc.totals.remaining)}</div></Card>
        </div>

        <h2 className="text-lg font-semibold text-slate-800 mb-2">{t("perStudent")}</h2>
        {acc.children.map((c) => (
          <Card key={c.student_id} className="card-float border-slate-200 p-4 mb-3" data-testid={`fa-student-${c.student_id}`}>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
              <div className="font-semibold text-slate-800">{c.student_name}</div>
              <div className="text-sm text-slate-500">{t("invoiced")}: {money(c.invoiced)} · {t("paid")}: {money(c.paid)} · <span className="text-amber-600">{t("remaining")}: {money(c.remaining)}</span></div>
            </div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50/70"><TableHead>{t("number")}</TableHead><TableHead>{t("category")}</TableHead><TableHead>{t("period")}</TableHead><TableHead className="text-right">{t("amount")}</TableHead><TableHead className="text-right">{t("remaining")}</TableHead><TableHead>{t("status")}</TableHead><TableHead className="text-right">PDF</TableHead></TableRow></TableHeader>
              <TableBody>
                {c.invoices.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-4">{t("noData")}</TableCell></TableRow> :
                  c.invoices.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell className="text-slate-700 text-sm">{i.number}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{t(`cat_${i.category}`)}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{i.period_label || "—"}</TableCell>
                      <TableCell className="text-right">{money(i.total)}</TableCell>
                      <TableCell className="text-right text-amber-700">{money(i.remaining)}</TableCell>
                      <TableCell><Badge className={`border-0 text-xs ${STATUS_STYLE[i.status]}`}>{t(`inv_${i.status}`)}</Badge></TableCell>
                      <TableCell className="text-right"><Button size="sm" variant="ghost" className="h-8" onClick={() => openPdf(`/finance/invoices/${i.id}/pdf`)}><FileText className="h-4 w-4" /></Button></TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Card>
        ))}

        <h2 className="text-lg font-semibold text-slate-800 mb-2 mt-4">{t("receipts")}</h2>
        <Card className="card-float border-slate-200 overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50/70"><TableHead>{t("number")}</TableHead><TableHead>{t("method")}</TableHead><TableHead className="text-right">{t("amount")}</TableHead><TableHead>{t("date")}</TableHead><TableHead className="text-right">PDF</TableHead></TableRow></TableHeader>
            <TableBody>
              {acc.receipts.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-4">{t("noData")}</TableCell></TableRow> :
                acc.receipts.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-slate-700 text-sm">{r.number}</TableCell>
                    <TableCell className="text-slate-500 text-sm">{t(`method_${r.method}`)}</TableCell>
                    <TableCell className="text-right">{money(r.amount)}</TableCell>
                    <TableCell className="text-slate-500 text-sm">{(r.date || "").slice(0, 10)}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" className="h-8" onClick={() => openPdf(`/finance/receipts/${r.id}/pdf`)}><FileText className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </>)}
    </div>
  );
}
